import React, { FC, useState, useMemo, useCallback } from "react";
import Pagination from "./Pagination";
import "sortable-table-pattern/dist/main.css";

export interface TableColumn {
  key: string;
  label: string;
  className?: string;
  isSortable?: boolean,
  sortColumn?: string;
}

export interface TableRow {
  [key: string]: React.ReactNode;
}

export interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
}

export interface TableProps {
  columns: TableColumn[];
  rows: TableRow[];
  onActionClick?: (rowData: TableRow) => void;
  showPagination?: boolean;
  paginationProps?: PaginationProps;
  caption?: string;
  actionKey?: string;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onSort?: (columnKey: string, sortDirection: 'ASC' | 'DESC') => void;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  className?: string;
}


const Table: FC<TableProps> = ({
  columns,
  rows,
  onActionClick,
  actionKey,
  showPagination = false,
  paginationProps,
  caption,
  currentPage: controlledPage,
  onPageChange,
  onSort,
  sortColumn,
  sortDirection,
  className,
}) => {
  const [internalPage, setInternalPage] = useState(1);

  const handleSort = useCallback((column: TableColumn) => {
    if (!onSort) return;
    const apiSortColumn = column.sortColumn ?? column.key;
    const newDirection = sortColumn === apiSortColumn && sortDirection === 'ASC' ? 'DESC' : 'ASC';
    onSort(apiSortColumn, newDirection);
  }, [onSort, sortColumn, sortDirection]);
  const currentPage = controlledPage ?? internalPage;

  const usePagination = showPagination && paginationProps;
  const itemsPerPage = usePagination ? paginationProps.itemsPerPage : rows.length;
  const totalItems = usePagination ? paginationProps.totalItems : rows.length;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const visibleRows = useMemo(() => rows.slice(startIndex, endIndex), [rows, startIndex, endIndex]);

  return (
    <div className="flex flex-col w-full">
      <table
        className={`ds-data-table ds-data-table--stripes-odd ${className || ''}`}
        data-ds-theme="vessel"
        data-ds-variant="basic"
        data-ds-version="1"
      >
        {caption && (
          <caption>
            <p className="--medium">{caption}</p>
          </caption>
        )}

        <thead>
          <tr>
            {columns.map((col, index) => {
              const apiSortColumn = col.sortColumn ?? col.key;
              const isActiveSort = sortColumn === apiSortColumn;
              let ariaSort: "ascending" | "descending" | undefined = undefined;
              if (isActiveSort) {
                ariaSort = sortDirection === 'ASC' ? 'ascending' : 'descending';
              }
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={col.className}
                  aria-sort={ariaSort}
                >
                  {col.isSortable && onSort ? (
                    <button
                      type="button"
                      className="th-btn -medium"
                      data-posinset={index + 1}
                      data-setsize={columns.length}
                      data-direction={isActiveSort ? sortDirection : undefined}
                      data-text={col.label}
                      onClick={() => handleSort(col)}
                    >
                      {col.label}
                      <img
                        className="arrows-table"
                        src={isActiveSort && sortDirection === 'ASC' ? '/assets/images/chevronup.svg' : '/assets/images/chevrondown.svg'}
                        alt=""
                        height="20"
                        width="20"
                      />
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {visibleRows.map((row) => {
            return (
              <tr
                key={(row.rowKey || row.id) as string}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`text-[14px]! ${col.className || ""}`}
                  >
                    <span className="table-head">
                      {col.label}
                    </span>
                    <span className="table-text">
                      { col.key === actionKey && onActionClick ? (
                       <button
                          type="button"
                          className={`-medium text-kp-blue-dark! text-left underline cursor-pointer bg-transparent border-0 p-0`}
                          data-analytics-type="link"
                          data-analytics-click="General click"
                          onClick={(e) => {
                            e.preventDefault();
                            onActionClick(row);
                          }}
                        >
                          {row[actionKey]}
                        </button>
                      ) : (
                        row[col.key]
                      )}
                    </span>
                  </td>
                  
                       
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {usePagination &&
        globalThis.window !== undefined &&
        globalThis.window.innerWidth >= 768 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange ?? setInternalPage}
          />
        )}
    </div>
  );
};

export default Table;
